import { styled } from "@mui/material";
import { Box } from "@mui/system";
import { createContext, useEffect } from "react";

import { APP_GRID, ROUTES } from "consts";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { DeployerPage } from "pages/deployer";
import analytics from "services/analytics";
import { Footer } from "components/footer";
import { Header } from "components/header";
import { useJettonLogo } from "hooks/useJettonLogo";
import useNotification from "hooks/useNotification";
import { Jetton } from "pages/jetton";
import { Management } from "pages/management";

analytics.init();

const HOME_TITLE = "TON一键发币工具｜创建Jetton代币 - BananaTool";
const HOME_DESCRIPTION =
  "BananaTool TON一键发币工具，支持在TON主网创建Jetton代币，设置名称、符号、精度、发行量与Logo，并提供铸币、销毁、元数据更新和权限管理。";

function setNamedMeta(name: string, content: string) {
  let meta = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = name;
    document.head.appendChild(meta);
  }
  meta.content = content;
}

const RouteSeo = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const isHome = pathname === "/";
    const canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');

    document.documentElement.lang = "zh-CN";
    document.title = isHome
      ? HOME_TITLE
      : pathname === ROUTES.management
        ? "TON代币管理 - BananaTool"
        : "TON Jetton合约管理 - BananaTool";
    setNamedMeta("description", isHome ? HOME_DESCRIPTION : "连接TON钱包并管理您的Jetton代币。");
    setNamedMeta(
      "robots",
      isHome
        ? "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1"
        : "noindex,follow",
    );
    setNamedMeta(
      "googlebot",
      isHome
        ? "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1"
        : "noindex,follow",
    );

    if (isHome) {
      const homeCanonical = canonical || document.createElement("link");
      homeCanonical.rel = "canonical";
      homeCanonical.href = "https://ton.bananatool.org/";
      if (!canonical) document.head.appendChild(homeCanonical);
    } else {
      canonical?.remove();
    }
  }, [pathname]);

  return null;
};

const AppWrapper = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  height: "100vh",
  overflowY: "scroll",
}));

const FooterBox = styled(Box)(() => ({
  display: "flex",
  flex: 1,
  alignItems: "flex-end",
  justifyContent: "center",
}));

const ScreensWrapper = styled(Box)({
  "*::-webkit-scrollbar": {
    display: "none",
  },
  "*::-webkit-scrollbar-track": {
    display: "none",
  },
  "*::-webkit-scrollbar-thumb": {
    display: "none",
  },
});

const FlexibleBox = styled(Box)(({ theme }) => ({
  maxWidth: APP_GRID,
  width: "calc(100% - 50px)",
  marginLeft: "auto",
  marginRight: "auto",

  [theme.breakpoints.down("sm")]: {
    width: "calc(100% - 30px)",
  },
}));

export const EnvContext = createContext({
  isSandbox: false,
  isTestnet: false,
});

const PageNotFound = () => {
  const { showNotification } = useNotification();

  useEffect(() => {
    showNotification("Page not found", "error");
  }, []);

  return <Box />;
};

interface ContentWrapperProps {
  children?: any;
}

const ContentWrapper = ({ children }: ContentWrapperProps) => {
  return (
    <FlexibleBox>
      {children}
      <Outlet />
    </FlexibleBox>
  );
};

const App = () => {
  const { resetJetton } = useJettonLogo();
  const location = useLocation();

  useEffect(() => {
    resetJetton();
  }, [location.pathname]);

  const isSandbox = window.location.search.includes("sandbox");
  const isTestnet = window.location.search.includes("testnet");

  return (
    <AppWrapper>
      <EnvContext.Provider
        value={{
          isSandbox: isSandbox,
          isTestnet: isTestnet,
        }}>
        <ScreensWrapper>
          <RouteSeo />
          <Routes>
            <Route
              path="*"
              element={
                <>
                  <Header />
                  <Navigate to="/" />
                  <PageNotFound />
                </>
              }
            />
            <Route path="/" element={<Header />}>
              <Route path="/" element={<ContentWrapper />}>
                <Route path={ROUTES.deployer} element={<DeployerPage />} />
                <Route path={ROUTES.jettonId} element={<Jetton />} />
                <Route path={ROUTES.management} element={<Management />} />
              </Route>
            </Route>
          </Routes>
        </ScreensWrapper>
      </EnvContext.Provider>
      <FooterBox mt={5}>
        <Footer />
      </FooterBox>
    </AppWrapper>
  );
};

export default App;
